import React from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    alpha,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import PostContent from '../PostContent';

/**
 * SchedulePostDrawer — right-side drawer for scheduling a post from the calendar.
 *
 * Props:
 *   open              — boolean
 *   onClose           — () => void
 *   onPost            — async (posts, mediaFiles) => results  (forwarded to PostContent)
 *   initialDate       — ISO string of the clicked calendar slot
 *   initialPlatforms  — string[] | null
 */
const SchedulePostDrawer = ({ open, onClose, onPost, initialDate, initialPlatforms }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const formattedDate = initialDate
        ? dayjs(initialDate).format('ddd, MMM D, YYYY')
        : '';

    const handlePost = async (posts, mediaFiles) => {
        if (!onPost) {
            console.error('SchedulePostDrawer: onPost handler is required but not provided.');
            return [];
        }
        const results = await onPost(posts, mediaFiles);
        // Auto-close drawer after a short delay if everything succeeded
        const allOk = results?.every?.(r => r.status === 'success' || r.status === 'queued');
        if (allOk) {
            setTimeout(onClose, 1500);
        }
        return results;
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: '85vw', md: 760, lg: 820 },
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.default',
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 3,
                    py: 2,
                    bgcolor: isDark
                        ? alpha(theme.palette.background.paper, 0.95)
                        : alpha(theme.palette.background.paper, 0.98),
                    backdropFilter: 'blur(8px)',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                }}
            >
                <CalendarMonthIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        Schedule a Post
                    </Typography>
                    {formattedDate && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Pre-filled for {formattedDate}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />

            {/* Scrollable body — PostContent renders the full posting form */}
            {/* key=initialDate forces a full remount when the user picks a different slot,
                preventing stale form state from a previous open from leaking through. */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
                {open && (
                    <PostContent
                        key={initialDate}
                        onPost={handlePost}
                        initialScheduledDate={initialDate}
                        initialPlatforms={initialPlatforms}
                    />
                )}
            </Box>
        </Drawer>
    );
};

export default SchedulePostDrawer;
